import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { jobService } from './JobService';

interface JobFormData {
  title: string;
  organization: string;
  location: string;
  type: string;
  close_date: string;
  department: string;
  salary: number | null;
  vacancy_number: string;
  gender: string;
  no_of_jobs: number;
  description: string;
  contact_email: string;
  is_active: boolean;
  order: number;
}

const JobForm: React.FC<{ isEdit?: boolean }> = ({ isEdit = false }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [isPolicySalary, setIsPolicySalary] = useState(false);

  const { register, handleSubmit, formState: { errors }, reset, setValue, watch } = useForm<JobFormData>({
    defaultValues: {
      title: '',
      organization: '',
      location: '',
      type: 'full_time',
      close_date: '',
      department: '',
      salary: null,
      vacancy_number: '',
      gender: 'any',
      no_of_jobs: 1,
      description: '',
      contact_email: '',
      is_active: true,
      order: 0
    }
  });

  const salaryValue = watch('salary');

  useEffect(() => {
    if (isEdit && id) {
      fetchJob();
    }
  }, [id, isEdit]);

  // Check if salary is null or 0 to set the checkbox state
  useEffect(() => {
    if (salaryValue === null || salaryValue === 0) {
      setIsPolicySalary(true);
    } else {
      setIsPolicySalary(false);
    }
  }, [salaryValue]);

  const fetchJob = async () => {
    try {
      setLoading(true);
      const response = await jobService.getById(parseInt(id!));
      
      if (response.success) {
        const data = response.data;
        
        let closeDate = data.close_date;
        if (closeDate && closeDate.includes('T')) {
          closeDate = closeDate.split('T')[0];
        }
        
        // Check if salary is null or 0 for policy
        if (data.salary === null || data.salary === 0) {
          setIsPolicySalary(true);
        }
        
        reset({
          title: data.title,
          organization: data.organization,
          location: data.location,
          type: data.type,
          close_date: closeDate,
          department: data.department || '',
          salary: data.salary,
          vacancy_number: data.vacancy_number || '',
          gender: data.gender,
          no_of_jobs: data.no_of_jobs,
          description: data.description || '',
          contact_email: data.contact_email || '',
          is_active: data.is_active,
          order: data.order
        });
      }
    } catch (error) {
      toast.error('Failed to fetch job details');
      navigate('/admin/home/jobs');
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (data: JobFormData) => {
    try {
      setLoading(true);
      
      console.log('Form data:', data);
      
      const payload: any = {
        title: data.title.trim(),
        organization: data.organization.trim(),
        location: data.location.trim(),
        type: data.type,
        close_date: data.close_date,
        department: data.department?.trim() || null,
        salary: data.salary ? Number(data.salary) : null, // Will be null if policy is set
        vacancy_number: data.vacancy_number?.trim() || null,
        gender: data.gender,
        no_of_jobs: Number(data.no_of_jobs),
        description: data.description?.trim() || null,
        contact_email: data.contact_email?.trim() || null,
        is_active: data.is_active,
        order: Number(data.order)
      };
      
      console.log('Sending payload:', payload);
      
      let response;
      if (isEdit && id) {
        response = await jobService.update(parseInt(id), payload);
      } else {
        response = await jobService.create(payload);
      }
      
      if (response.success) {
        toast.success(`Job ${isEdit ? 'updated' : 'created'} successfully`);
        navigate('/admin/home/jobs');
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      
      if (error.response?.data?.errors) {
        const errors = error.response.data.errors;
        Object.keys(errors).forEach(key => {
          toast.error(`${key}: ${errors[key].join(', ')}`);
        });
      } else {
        toast.error(error.response?.data?.message || 'Something went wrong');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle policy checkbox change
  const handlePolicyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsPolicySalary(checked);
    
    if (checked) {
      // Set salary to null
      setValue('salary', null);
    } else {
      // User wants to enter custom salary, set to empty
      setValue('salary', null);
      // Focus the salary input
      const salaryInput = document.querySelector('input[name="salary"]') as HTMLInputElement;
      if (salaryInput) {
        salaryInput.focus();
      }
    }
  };

  if (loading && isEdit) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <Link to="/admin/home/jobs" className="inline-flex items-center text-blue-600 hover:text-blue-700 mb-4">
          <ArrowLeft size={18} className="mr-2" />
          Back to Jobs
        </Link>
        <h1 className="text-3xl font-bold text-gray-800">
          {isEdit ? 'Edit Job' : 'Create New Job'}
        </h1>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Left Column */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Title *</label>
              <input
                type="text"
                {...register('title', { required: 'Title is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Program Manager"
              />
              {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Organization *</label>
              <input
                type="text"
                {...register('organization', { required: 'Organization is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., NORA Afghanistan"
              />
              {errors.organization && <p className="mt-1 text-sm text-red-600">{errors.organization.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Location *</label>
              <input
                type="text"
                {...register('location', { required: 'Location is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Kabul"
              />
              {errors.location && <p className="mt-1 text-sm text-red-600">{errors.location.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Job Type *</label>
              <select
                {...register('type', { required: 'Job type is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Department</label>
              <input
                type="text"
                {...register('department')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., Programs"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Number of Positions *</label>
              <input
                type="number"
                min="1"
                {...register('no_of_jobs', { 
                  required: 'Number of positions is required', 
                  min: 1,
                  valueAsNumber: true 
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.no_of_jobs && <p className="mt-1 text-sm text-red-600">{errors.no_of_jobs.message}</p>}
            </div>
          </div>

          {/* Right Column */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Closing Date *</label>
              <input
                type="date"
                {...register('close_date', { required: 'Closing date is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {errors.close_date && <p className="mt-1 text-sm text-red-600">{errors.close_date.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Salary (AFN)</label>
              <div className="space-y-2">
                <input
                  type="number"
                  step="1"
                  min="0"
                  {...register('salary', { 
                    valueAsNumber: true,
                    setValueAs: (v) => {
                      if (v === '' || v === null || v === undefined) return null;
                      const num = parseInt(v);
                      return isNaN(num) ? null : num;
                    }
                  })}
                  className={`w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 ${isPolicySalary ? 'bg-gray-100 text-gray-500' : ''}`}
                  placeholder={isPolicySalary ? "As per organization policy" : "Enter salary amount"}
                  disabled={isPolicySalary}
                />
                <label className="text-xs text-gray-500 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={isPolicySalary}
                    onChange={handlePolicyChange}
                    className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  As per organization policy
                </label>
              </div>
              {errors.salary && <p className="mt-1 text-sm text-red-600">{errors.salary.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Vacancy Number</label>
              <input
                type="text"
                {...register('vacancy_number')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="e.g., PM-2026-001"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Gender Preference *</label>
              <select
                {...register('gender', { required: 'Gender preference is required' })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              >
                <option value="any">Any</option>
                <option value="male">Male</option>
                <option value="female">Female</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Contact Email</label>
              <input
                type="email"
                {...register('contact_email')}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="hr@nora.org.af"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Display Order</label>
              <input
                type="number"
                min="0"
                {...register('order', { 
                  valueAsNumber: true,
                  min: 0 
                })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Full Width Fields */}
        <div className="mt-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Job Description</label>
            <textarea
              rows={6}
              {...register('description')}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              placeholder="Enter detailed job description..."
            />
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              {...register('is_active')}
              className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <label className="ml-2 text-sm text-gray-700">Active (visible on website)</label>
          </div>
        </div>

        <div className="mt-8 flex items-center space-x-4">
          <button
            type="submit"
            disabled={loading}
            className="px-6 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Save size={18} />}
            <span>{loading ? 'Saving...' : (isEdit ? 'Update Job' : 'Create Job')}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/admin/home/jobs')}
            className="px-6 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default JobForm;